import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { List, Set } from 'immutable';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { EntityTypesEnum } from '../../../../app.enum';
import {
  AppointmentFiltersEnum,
  ITimeEntry,
} from '../../../appointment-data/appointment-data.data';
import { AppointmentDataDispatcher } from '../../../appointment-data/appointment-data.dispatcher';
import { AppointmentDataStore } from '../../../appointment-data/appointment-data.store';
import { DialogActionsEnum, IDialogTrigger } from '../../../dialog/dialog.data';
import { DialogService } from '../../../dialog/dialog.service';
import { IEntityDataDispatcher } from '../../../entity-data/entity-data-dispatcher.interface';
import {
  ISearchParams,
  SEARCH_LIST_FILTER,
  SortByEnum,
  SortOrderEnum,
} from '../../../entity-data/entity-data.data';
import { IEntityStore } from '../../../entity-data/entity-store.interface';
import { INoItemsOptions } from '../../../feed/feed.interface';
import { SearchActionTriggersEnum } from '../../../search/search.component';
import { AmPmTimePipe } from '../../../shared/pipes/am-pm-time/am-pm-time.pipe';
import { FriendlyDatePipe } from '../../../shared/pipes/friendly-date/friendly-date.pipe';
import { FriendlyDurationPipe } from '../../../shared/pipes/friendly-duration/friendly-duration.pipe';
import {
  DateTimeService,
  MOMENT_LONG_ABBREVIATED_DATE_FORMAT,
} from 'i2g-ng-utils';
import { TrackedTimeFiltersEnum } from '../../../tracked-time-data/tracked-time-data.data';
import { TrackedTimeDataDispatcher } from '../../../tracked-time-data/tracked-time-data.dispatcher';
import { TrackedTimeDataStore } from '../../../tracked-time-data/tracked-time-data.store';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';
import { TrackingObjectBuilder } from '../../../tracking/tracking-object.builder';
import {
  ITimeEntriesMultipleSelectAdditionalProperties,
  ITrackingAction,
  ITrackingObject,
  TrackingDialogNamesEnum,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { TimeEntryDialogBuilder } from './time-entry-picker-dialog.builder';
import {
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  take,
  takeUntil,
} from 'rxjs/operators';

export const DIALOG_TYPE = 'time-entry-picker';

@Component({
  selector: 'app-time-entry-picker-dialog',
  templateUrl: './time-entry-picker-dialog.component.html',
  styleUrls: ['./time-entry-picker-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [TimeEntryDialogBuilder],
})
export class TimeEntryPickerDialogComponent
  implements OnInit, OnChanges, OnDestroy {
  @Input() maxItemsAllowed: number;
  @Input() clientId: string;
  @Input() timeEntryIdsAdded: List<string>;
  @Input() trackingScreenName: TrackingScreenNamesEnum;
  @Input() timeEntryType:
    | EntityTypesEnum.tracked_time
    | EntityTypesEnum.appointment;

  @Output() onSelectTimeEntries: EventEmitter<
    List<ITimeEntry>
  > = new EventEmitter<List<ITimeEntry>>();

  private _destroyed: Subject<boolean> = new Subject<boolean>();
  private _inSearchMode: boolean;
  private _bulkActionControls: { [itemId: string]: FormControl } = {};
  private _selectedTimeEntries: Set<ITimeEntry>;
  private _timeEntryDataDispatcher: IEntityDataDispatcher;
  private _timeEntryDataStore: IEntityStore;

  dialogType: string;
  headerLabel: string;
  noTimeEntriesOptions: INoItemsOptions;
  boostedTimeEntries$: Observable<List<ITimeEntry>>;
  filteredTimeEntries$: Observable<List<ITimeEntry>>;
  isAppointment$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    void 0
  );
  searchActionTriggers$: BehaviorSubject<SearchActionTriggersEnum> = new BehaviorSubject<SearchActionTriggersEnum>(
    void 0
  );
  numberOfSelectedTimeEntries = 0;
  boostedClientName: string;
  dialogButtonText: string;
  error: string;
  trackingObject: ITrackingObject;
  addBulkAction: ITrackingAction;
  timeEntriesMultipleSelectTrackingProperties: ITimeEntriesMultipleSelectAdditionalProperties;

  constructor(
    private _timeEntryDialogBuilder: TimeEntryDialogBuilder,
    private _trackedTimeDataStore: TrackedTimeDataStore,
    private _trackedTimeDataDispatcher: TrackedTimeDataDispatcher,
    private _appointmentDataStore: AppointmentDataStore,
    private _appointmentDataDispatcher: AppointmentDataDispatcher,
    private _dateTimeService: DateTimeService,
    private _translateService: TranslateService,
    private _dialogService: DialogService,
    private _amPmTimePipe: AmPmTimePipe,
    private _friendlyDatePipe: FriendlyDatePipe,
    private _friendlyDurationPipe: FriendlyDurationPipe,
    private _changeDetectorRef: ChangeDetectorRef,
    private _trackingObjectBuilder: TrackingObjectBuilder,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes.timeEntryType ||
      changes.clientId ||
      changes.timeEntryIdsAdded
    ) {
      this.isAppointment$.next(
        this.timeEntryType === EntityTypesEnum.appointment
      );

      if (this.isAppointment$.value) {
        this._timeEntryDataDispatcher = this._appointmentDataDispatcher;
        this._timeEntryDataStore = this._appointmentDataStore;

        this.headerLabel = this._translateService.instant(
          'LABEL.ADD_APPOINTMENTS'
        );
        this.trackingObject = this._trackingObjectBuilder.buildDialog(
          TrackingDialogNamesEnum.appointments_multiple_select
        );
        this.noTimeEntriesOptions = {
          description: this._translateService.instant('LABEL.NO_APPOINTMENTS'),
        };
      } else {
        this._timeEntryDataDispatcher = this._trackedTimeDataDispatcher;
        this._timeEntryDataStore = this._trackedTimeDataStore;

        this.headerLabel = this._translateService.instant(
          'LABEL.ADD_TIME_ENTRY'
        );
        this.trackingObject = this._trackingObjectBuilder.buildDialog(
          TrackingDialogNamesEnum.time_entries_multiple_select
        );
        this.noTimeEntriesOptions = {
          description: this._translateService.instant('LABEL.NO_TIME_ENTRIES'),
        };
      }

      this.searchUnbilledTimeEntries();
    }
  }

  ngOnInit() {
    this.addBulkAction = this._trackingActionBuilder.buildButtonTapped(
      'add_bulk',
      this.trackingScreenName
    );
    this.dialogType = DIALOG_TYPE;

    const results$ = this.isAppointment$.pipe(
      distinctUntilChanged(),
      switchMap((isAppointment: boolean) =>
        isAppointment
          ? this._appointmentDataStore.getSortedList(SEARCH_LIST_FILTER)
          : this._trackedTimeDataStore.getSortedList(SEARCH_LIST_FILTER)
      ),
      map(
        (
          timeEntries: List<ITimeEntry> // filter time entries that already have been added
        ) =>
          timeEntries &&
          (timeEntries.filter(
            (timeEntry: ITimeEntry) =>
              !this.timeEntryIdsAdded ||
              this.timeEntryIdsAdded.findIndex(
                (timeEntryId: string) => timeEntryId === timeEntry.id
              ) < 0
          ) as List<ITimeEntry>)
      )
    );

    // boosted results for current client
    this.boostedTimeEntries$ = results$.pipe(
      map(
        (timeEntries: List<ITimeEntry>) =>
          timeEntries &&
          (timeEntries.filter(
            (timeEntry: ITimeEntry) =>
              !this._inSearchMode &&
              timeEntry.clientReference.id === this.clientId
          ) as List<ITimeEntry>)
      )
    );

    // other results
    this.filteredTimeEntries$ = results$.pipe(
      map(
        (timeEntries: List<ITimeEntry>) =>
          timeEntries &&
          (timeEntries.filter(
            (timeEntry: ITimeEntry) =>
              this._inSearchMode ||
              timeEntry.clientReference.id !== this.clientId
          ) as List<ITimeEntry>)
      )
    );

    // set boosted client name
    this.boostedTimeEntries$
      .pipe(takeUntil(this._destroyed))
      .subscribe((timeEntries: List<ITimeEntry>) => {
        this.boostedClientName =
          timeEntries && timeEntries.size > 0
            ? timeEntries.first().billingName
            : null;

        this._changeDetectorRef.markForCheck();
      });

    // update selected entities
    this.isAppointment$
      .pipe(
        distinctUntilChanged(),
        switchMap((isAppointment: boolean) =>
          isAppointment
            ? this._appointmentDataStore.getSelectedEntities()
            : this._trackedTimeDataStore.getSelectedEntities()
        ),
        takeUntil(this._destroyed)
      )
      .subscribe((timeEntries: Set<ITimeEntry>) => {
        this._selectedTimeEntries = timeEntries || Set([]);

        this.numberOfSelectedTimeEntries =
          (timeEntries && timeEntries.size) || 0;

        this._changeDetectorRef.markForCheck();
      });

    // on open dialog
    this._dialogService.dialogTriggers$
      .pipe(
        filter((dialogTrigger: IDialogTrigger) => {
          return (
            dialogTrigger.type === this.dialogType &&
            dialogTrigger.action === DialogActionsEnum.open
          );
        }),
        takeUntil(this._destroyed)
      )
      .subscribe(() => {
        this._inSearchMode = false;

        this.resetSelectedTimeEntries();

        this._timeEntryDataStore
          .getListFetched(SEARCH_LIST_FILTER)
          .pipe(
            takeUntil(this._destroyed),
            take(1),
            filter((fetched: boolean) => !fetched)
          )
          .subscribe((fetched: boolean) => this.searchUnbilledTimeEntries());
      });
  }

  onSearch(searchTerm: string) {
    this.resetSelectedTimeEntries();

    if (searchTerm) {
      this._inSearchMode = true;

      this.searchUnbilledTimeEntries(searchTerm.toLowerCase());
    } else {
      this._inSearchMode = false;

      this.searchUnbilledTimeEntries();
    }
  }

  searchUnbilledTimeEntries(query?: string) {
    const extraParams: ISearchParams = {
      list_category: this.isAppointment$.value
        ? AppointmentFiltersEnum.unbilled
        : TrackedTimeFiltersEnum.unbilled,
      ...(this.clientId ? { client_id: this.clientId } : {}),
    };

    this._timeEntryDataDispatcher.searchList(
      query,
      SortByEnum.start_date,
      SortOrderEnum.desc,
      false,
      false,
      false,
      extraParams
    );
  }

  selectAllBoostedTimeEntries() {
    this.boostedTimeEntries$
      .pipe(
        takeUntil(this._destroyed),
        filter((x) => !!x),
        take(1)
      )
      .subscribe((timeEntries: List<ITimeEntry>) => {
        timeEntries.forEach((timeEntry: ITimeEntry) => {
          this._bulkActionControls[timeEntry.id].setValue(true, {
            emitEvent: true,
          });
        });
      });
  }

  onClickTimeEntry(timeEntry: ITimeEntry) {
    const currentValue = this._bulkActionControls[timeEntry.id].value;

    this._bulkActionControls[timeEntry.id].setValue(!currentValue, {
      emitEvent: true,
    });
  }

  getBulkActionControl(timeEntry: ITimeEntry): FormControl {
    const timeEntryId = timeEntry.id;
    const selectedTimeEntry = this._selectedTimeEntries.find(
      (timeEntry: ITimeEntry) => timeEntry.id === timeEntryId
    );
    const isSelected = !!selectedTimeEntry;

    if (!this._bulkActionControls) {
      this._bulkActionControls = {};
    }

    if (!this._bulkActionControls[timeEntryId]) {
      this._bulkActionControls[timeEntryId] = new FormControl(isSelected);

      this._bulkActionControls[timeEntryId].valueChanges
        .pipe(takeUntil(this._destroyed))
        .subscribe((value: boolean) =>
          this.onBulkActionSelect(timeEntry, value)
        );
    } else {
      this._bulkActionControls[timeEntryId].setValue(isSelected, {
        emitEvent: false,
      });
    }

    return this._bulkActionControls[timeEntryId];
  }

  onBulkActionSelect(timeEntry: ITimeEntry, selected: boolean) {
    const timeEntriesAdded =
      (this.timeEntryIdsAdded && this.timeEntryIdsAdded.size) || 0;
    const belowLimit =
      this.numberOfSelectedTimeEntries <
      this.maxItemsAllowed - timeEntriesAdded;

    this.error = null;

    if (selected) {
      if (belowLimit) {
        this._timeEntryDataDispatcher.addToSelectedList(timeEntry);
      } else {
        this.error = this._translateService.instant(
          'WARNING.BULK_SELECT_LIMIT',
          {
            maxNumber: this.maxItemsAllowed,
          }
        );

        this._bulkActionControls[timeEntry.id].setValue(false, {
          emitEvent: false,
        });

        this._timeEntryDataDispatcher.removeFromSelectedList(timeEntry);
      }
    } else {
      this._timeEntryDataDispatcher.removeFromSelectedList(timeEntry);
    }

    if (this.numberOfSelectedTimeEntries <= 1) {
      this.dialogButtonText = this.isAppointment$.value
        ? this._translateService.instant('ACTION.ADD_APPOINTMENT_SINGLE')
        : this._translateService.instant('ACTION.ADD_TIME_ENTRY_SINGLE');
    } else {
      this.dialogButtonText = this.isAppointment$.value
        ? this._translateService.instant('ACTION.ADD_APPOINTMENT_MULTIPLE', {
            count: this.numberOfSelectedTimeEntries,
          })
        : this._translateService.instant('ACTION.ADD_TIME_ENTRY_MULTIPLE', {
            count: this.numberOfSelectedTimeEntries,
          });
    }

    this.timeEntriesMultipleSelectTrackingProperties = {
      count: this.numberOfSelectedTimeEntries,
    };

    this._changeDetectorRef.markForCheck();
  }

  saveSelectedTimeEntries() {
    this.onSelectTimeEntries.emit(this._selectedTimeEntries.toList());

    this.resetSelectedTimeEntries();
  }

  resetSelectedTimeEntries() {
    this.error = null;
    this.dialogButtonText = this.isAppointment$.value
      ? this._translateService.instant('ACTION.ADD_APPOINTMENT_SINGLE')
      : this._translateService.instant('ACTION.ADD_TIME_ENTRY_SINGLE');

    this._timeEntryDataDispatcher.resetSelectedList();
  }

  onClose() {
    if (this._inSearchMode) {
      this.searchActionTriggers$.next(SearchActionTriggersEnum.reset);
    }
  }

  groupByFn = this._timeEntryDialogBuilder.buildGroupByFn();

  trackByFn = (index: number, timeEntry: ITimeEntry) => timeEntry.id;

  getAppointmentSubDescription(timeEntry: ITimeEntry): string {
    const startDate = timeEntry.startDate;
    const endDate = this._dateTimeService.addTime(
      startDate,
      timeEntry.duration,
      'seconds',
      null,
      false,
      false
    );
    const startTime = this._amPmTimePipe.transform(startDate);
    const endTime = this._amPmTimePipe.transform(endDate);
    const duration = this._friendlyDurationPipe.transform(timeEntry.duration);
    const date = this._friendlyDatePipe.transform(
      startDate,
      true,
      0,
      0,
      MOMENT_LONG_ABBREVIATED_DATE_FORMAT
    );

    return this._translateService.instant(
      'DIALOG.TIME_ENTRY_PICKER.APPOINTMENT_SUB_DESCRIPTION',
      {
        startTime,
        endTime,
        duration,
        date,
      }
    );
  }

  ngOnDestroy() {
    this._timeEntryDataDispatcher.clearList(SEARCH_LIST_FILTER);

    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
